import { EChartsOption } from 'echarts'
import ReactEcharts from 'echarts-for-react'
import { PieDataItemOption } from 'echarts/types/src/chart/pie/PieSeries'
import { FC, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { Translation } from 'cng-common/features/translations/components/translation'
import { useSafetyTranslation } from 'cng-common/features/translations/hooks'
import { selectTranslationMode } from 'cng-common/features/translations/selectors'

import { TRANSLATION_KEYS } from '../../translations'

import { ICompletenessChartProps } from './models/completenessChartProps'

import styles from './Completeness.module.scss'

export const CompletenessChart: FC<ICompletenessChartProps> = ({
  countOfFullyAcknowledgedPartners,
  countOfPartnersWith100PercentCompleteness,
  countOfPartnerWithLess100PercentCount,
  countOfPartnersWithLess50PercentCompleteness,
}) => {
  const t = useSafetyTranslation()
  const translationMode = useSelector(selectTranslationMode)

  const totalSum = useMemo(() => {
    return [
      countOfFullyAcknowledgedPartners,
      countOfPartnersWith100PercentCompleteness,
      countOfPartnerWithLess100PercentCount,
      countOfPartnersWithLess50PercentCompleteness,
    ].reduce((result, value) => result + value, 0)
  }, [
    countOfFullyAcknowledgedPartners,
    countOfPartnersWith100PercentCompleteness,
    countOfPartnerWithLess100PercentCount,
    countOfPartnersWithLess50PercentCompleteness,
  ])

  const option = useMemo<EChartsOption>(() => {
    const chartTitle = t(TRANSLATION_KEYS.globalPartnerExpanded.completenessChart.chartTitle)
    const fullyAcknowledgeLabel = t(
      TRANSLATION_KEYS.globalPartnerExpanded.completenessChart.fullyAcknowledged
    )
    const equals100Percent = t(
      TRANSLATION_KEYS.globalPartnerExpanded.completenessChart.equals100Percent
    )
    const lessThen100Percent = t(
      TRANSLATION_KEYS.globalPartnerExpanded.completenessChart.lessThen100Percent
    )
    const lessThen50Percent = t(
      TRANSLATION_KEYS.globalPartnerExpanded.completenessChart.lessThen50Percent
    )

    const chartData = [
      countOfFullyAcknowledgedPartners > 0
        ? {
            name: fullyAcknowledgeLabel,
            value: countOfFullyAcknowledgedPartners,
          }
        : null,
      countOfPartnersWith100PercentCompleteness > 0
        ? {
            name: equals100Percent,
            value: countOfPartnersWith100PercentCompleteness,
          }
        : null,
      countOfPartnerWithLess100PercentCount > 0
        ? {
            name: lessThen100Percent,
            value: countOfPartnerWithLess100PercentCount,
          }
        : null,
      countOfPartnersWithLess50PercentCompleteness > 0
        ? {
            name: lessThen50Percent,
            value: countOfPartnersWithLess50PercentCompleteness,
          }
        : null,
    ].filter((it) => it !== null) as PieDataItemOption[]

    return {
      legend: {
        bottom: '-1%',
        left: 'center',
      },
      title: {
        subtext: chartTitle,
        subtextStyle: {
          fontSize: 16,
        },
      },
      tooltip: {
        trigger: 'item',
      },
      series: [
        {
          type: 'pie',
          radius: ['40%', '70%'],
          data: chartData,
          label: {
            show: false,
          },
        },
      ],
    }
  }, [
    t,
    countOfFullyAcknowledgedPartners,
    countOfPartnersWith100PercentCompleteness,
    countOfPartnerWithLess100PercentCount,
    countOfPartnersWithLess50PercentCompleteness,
  ])

  if (translationMode === 'edit') {
    return (
      <div className={styles.root}>
        <Translation text={TRANSLATION_KEYS.globalPartnerExpanded.completenessChart.chartTitle} />
        <Translation
          text={TRANSLATION_KEYS.globalPartnerExpanded.completenessChart.fullyAcknowledged}
        />
        <Translation
          text={TRANSLATION_KEYS.globalPartnerExpanded.completenessChart.equals100Percent}
        />
        <Translation
          text={TRANSLATION_KEYS.globalPartnerExpanded.completenessChart.lessThen100Percent}
        />
        <Translation
          text={TRANSLATION_KEYS.globalPartnerExpanded.completenessChart.lessThen50Percent}
        />
      </div>
    )
  }

  return (
    <div className={styles.root}>
      <ReactEcharts option={option} opts={{ height: 300 }} />
      <div className={styles.totalContainer}>
        <div className={styles.totalValue}>{totalSum}</div>
        <div className={styles.totalLabel}>Total</div>
      </div>
    </div>
  )
}
